import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

// Hidden admin login route. Not linked from public navigation.
// Admin accounts are provisioned only via server-side seeding (see .env ADMIN_EMAIL/ADMIN_PASSWORD).
export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role !== "admin") {
        // Non-admin accounts blocked here — redirect to public login
        toast.error("This entrance is for admins only.");
        nav("/login");
        return;
      }
      toast.success(`Welcome, ${user.name} — control room ready.`);
      nav("/admin");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-24">
        <div className="glass rounded-3xl p-8 border border-magenta/30">
          <div className="inline-flex items-center gap-2 rounded-full bg-magenta text-white px-3 py-1 font-mono text-[10px] uppercase tracking-widest mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Internal · Admin
          </div>
          <h1 className="font-display text-3xl md:text-4xl uppercase leading-[0.95]">Eventom control room.</h1>
          <p className="mt-2 text-xs text-muted font-mono">Restricted access. Admin accounts are provisioned internally.</p>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input required data-testid="admin-login-email" type="email" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" autoComplete="username" />
            <input required data-testid="admin-login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark" autoComplete="current-password" />
            <button data-testid="admin-login-submit" disabled={loading} className="btn-primary w-full justify-center">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {loading ? "Signing in..." : "Enter"}
            </button>
          </form>
          <div className="mt-6 text-[10px] font-mono uppercase tracking-widest text-muted">
            Not an admin? Return to <a href="/login" className="text-lime underline">public login</a>.
          </div>
        </div>
      </div>
    </Layout>
  );
}
